/**
 * Перемикач «Сьогодні N» у панелі стартового екрана (start.html, позначка 6) і лічильник
 * «Задачі N» у пункті навігації задач (tasks-today.md, розд. 3): прострочені й сьогоднішні з тієї
 * самої відповіді, межа «99+», червоний — коли є прострочені (і словом у доступній назві).
 */
import { useQuery } from '@tanstack/react-query';
import { useNow } from '../../app/now.ts';
import { list as tl } from '../../i18n/uk/list.ts';
import { today as t } from '../../i18n/uk/today.ts';
import { useTodayPanel } from '../../shell/today-context.ts';
import { Icon } from '../../ui/Icon.tsx';
import type { MenuItem } from '../../ui/Menu.tsx';
import { type TaskCount, taskCount } from './model.ts';
import { type TodaySource, todayQueryOf } from './source.ts';

/** Лічильник задач; `null` — даних ще немає (або панелі немає): нічого не показувати. */
export function useTaskCount(source: TodaySource | null): TaskCount | null {
  const now = useNow();
  const { data } = useQuery({
    ...todayQueryOf(source ?? NONE),
    enabled: source !== null,
  });
  return source ? taskCount(data, now) : null;
}

const NONE: TodaySource = {
  key: ['today', 'none'],
  live: false,
  fetch: () => Promise.reject(new Error('no source')),
  complete: () => Promise.reject(new Error('no source')),
  reschedule: () => Promise.reject(new Error('no source')),
  undo: () => Promise.reject(new Error('no source')),
};

export function TaskCountBadge({ count }: { count: TaskCount }) {
  return (
    <span
      className={count.overdue > 0 ? 'count count--danger' : 'count count--accent'}
      title={t.tasksCount(count.text, count.overdue)}
    >
      <span aria-hidden="true">{count.text}</span>
      <span className="sr-only">{t.tasksCount(count.text, count.overdue)}</span>
    </span>
  );
}

export function TodayToggle() {
  const today = useTodayPanel();
  const count = useTaskCount(today.here ? today.source : null);
  if (!today.here || !today.source) return null;
  return (
    <button
      className="btn"
      type="button"
      aria-pressed={today.open}
      onClick={() => today.setOpen(!today.open)}
      data-testid="today-toggle"
    >
      <Icon name="task" />
      {tl.todayToggle}
      {count && count.total > 0 && <TaskCountBadge count={count} />}
    </button>
  );
}

/**
 * Той самий перемикач пунктом «⋯» панелі подання, коли кнопка не вміщується в рядок дій
 * (ViewToolbar, аудит якості № 11). Панелі на екрані немає — пунктів немає.
 */
export function useTodayToggleItem(): MenuItem[] {
  const today = useTodayPanel();
  if (!today.here || !today.source) return [];
  return [
    {
      kind: 'checkbox',
      id: 'today-toggle',
      label: tl.todayToggle,
      checked: today.open,
      onSelect: () => today.setOpen(!today.open),
    },
  ];
}
